import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Form, ListGroup, Row, Spinner, Table } from 'react-bootstrap';
import airbnbApi from './api';
import { ErrorAlert, Notice, PageLoader, clearMasterDataCache } from './common';

const MAX_ROWS = 500;

const MasterData = () => {
  const [categories, setCategories] = useState(null);
  const [category, setCategory] = useState(null);
  const [codes, setCodes] = useState(null);
  const [parentCode, setParentCode] = useState('');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadCategories = () => airbnbApi.masterDataCategories().then(setCategories).catch(setError);

  useEffect(() => { loadCategories(); }, []);

  useEffect(() => {
    if (!category) return;
    // ~77k regions: only load them for one state at a time
    if (category === 'regions' && !parentCode) {
      setCodes([]);
      return;
    }
    setCodes(null);
    const params = { category };
    if (parentCode) params.parent_code = parentCode;
    airbnbApi.masterData(params).then(setCodes).catch(setError);
  }, [category, parentCode, reloadKey]);

  const filtered = useMemo(() => {
    if (!codes) return [];
    const term = search.trim().toLowerCase();
    if (!term) return codes;
    return codes.filter(c => c.code.toLowerCase().includes(term) || (c.name || '').toLowerCase().includes(term));
  }, [codes, search]);

  const sync = () => {
    if (!window.confirm('Download all code lists from NextPax now? This replaces the current master data.')) return;
    setSyncing(true);
    setError(null);
    airbnbApi.syncMasterData()
      .then(result => {
        clearMasterDataCache();
        setNotice(`Synced ${result.synced} codes from NextPax.`);
        loadCategories();
        setReloadKey(k => k + 1);
      })
      .catch(setError)
      .finally(() => setSyncing(false));
  };

  const selectCategory = c => {
    setCategory(c);
    setParentCode('');
    setSearch('');
  };

  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13"><Link to="/airbnb">Property managers</Link> / Master data</div>
      <div className="d-flex justify-content-between align-items-center margbot15">
        <h4 className="mb-0">NextPax master data</h4>
        <Button size="sm" onClick={sync} disabled={syncing}>
          {syncing ? <><Spinner animation="border" size="sm" /> Syncing…</> : 'Sync now'}
        </Button>
      </div>
      <p className="fnt13 text-muted">
        Code lists from NextPax (countries, currencies, property types, Airbnb policies and settings…) used by the
        dropdowns and validations. Refreshed monthly; sync by hand if a code is missing.
      </p>
      <ErrorAlert error={error} onClose={() => setError(null)} />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      {!categories && !error && <PageLoader />}
      {categories && categories.length === 0 && (
        <div className="pad20 tc text-muted">No master data yet. Click "Sync now" to download it from NextPax.</div>
      )}
      {categories && categories.length > 0 && (
        <Row>
          <Col md={3}>
            <ListGroup className="margbot15">
              {categories.map(c => (
                <ListGroup.Item key={c} action active={c === category} onClick={() => selectCategory(c)}>{c}</ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
          <Col md={9}>
            {!category && <div className="text-muted pad20">Select a category.</div>}
            {category && (
              <Card>
                <Card.Header className="bld">{category}</Card.Header>
                <Card.Body>
                  <Row className="margbot10">
                    <Col md={6}>
                      <Form.Control placeholder="Search code or name" value={search} onChange={e => setSearch(e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Control placeholder="Parent code (e.g. IN, AIR298)" value={parentCode}
                        onChange={e => setParentCode(e.target.value.trim())} />
                    </Col>
                  </Row>
                  {!codes && <PageLoader />}
                  {codes && (
                    <>
                      {category === 'regions' && !parentCode && (
                        <div className="fnt13 text-muted margbot5">Enter a state code as parent (e.g. IN_DL) to list its regions.</div>
                      )}
                      <div className="fnt12 text-muted margbot5">
                        {filtered.length} code(s){filtered.length > MAX_ROWS && `, showing the first ${MAX_ROWS}`}
                      </div>
                      <Table bordered size="sm" className="bg1 mb-0">
                        <thead><tr><th>Code</th><th>Name</th><th>Parent</th></tr></thead>
                        <tbody>
                          {filtered.slice(0, MAX_ROWS).map(c => (
                            <tr key={`${c.parent_code}-${c.code}`}>
                              <td><code>{c.code}</code></td>
                              <td>{c.name || '—'}</td>
                              <td>{c.parent_code || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default MasterData;
